import { useEffect, useState } from 'react';
import { AlertCircle, Loader2, Building2, UserRound, Stethoscope, Handshake, Percent, Users, Save, X } from 'lucide-react';
import { DoctorSearchSelect } from './DoctorSearchSelect';
import { EmployeeSearchSelect } from './EmployeeSearchSelect';
import { DesignationSearchSelect } from './DesignationSearchSelect';
import { ValidatedNumberInput } from './validated-number-input';
import { SectionHeader, TypeCard } from './modal-kit';
import { useQuickCreateReferralPartner } from '../../services/httpServices/queries';
import type { ReferralPartnerType, ReferralPartner } from '../../services/httpServices/referralService';

interface QuickReferralModalProps {
  open: boolean;
  /** The text typed into the referral search, used to prefill the form. */
  initialQuery?: string;
  onClose: () => void;
  onCreated: (partner: ReferralPartner) => void;
}

const inputClass =
  'w-full px-3 py-2 rounded-lg border border-border bg-background text-text placeholder:text-text-muted/60 focus:outline-none focus:ring-2 focus:ring-primary text-sm transition-colors';
const labelClass = 'block text-xs font-medium text-text-muted mb-1';

/**
 * Minimal create dialog shared by the OPD receipt so a previously-unknown
 * referrer can be registered without leaving the counter. Registers a
 * first-class referral partner that is either a **Doctor** (linked to a doctor)
 * or **Other**. The server is idempotent: an existing partner with the same
 * doctor/mobile is reused.
 */
export function QuickReferralModal({ open, initialQuery = '', onClose, onCreated }: QuickReferralModalProps) {
  const [type, setType] = useState<ReferralPartnerType>('OTHER');
  const [name, setName] = useState('');
  const [mobile, setMobile] = useState('');
  const [organization, setOrganization] = useState('');
  const [designation, setDesignation] = useState('');
  const [defaultSharePercent, setDefaultSharePercent] = useState(0);
  const [employeeId, setEmployeeId] = useState('');
  const [doctor, setDoctor] = useState<any>();
  const [employee, setEmployee] = useState<any>();
  const [error, setError] = useState<string | null>(null);
  const mutation = useQuickCreateReferralPartner();

  // Reset + prefill whenever the dialog opens.
  useEffect(() => {
    if (!open) return;
    setType('OTHER');
    setName(initialQuery || '');
    setMobile(/^[+\d][\d\s+-]*$/.test(initialQuery) ? initialQuery : '');
    setOrganization('');
    setDesignation('');
    setDefaultSharePercent(0);
    setEmployeeId('');
    setDoctor(undefined);
    setEmployee(undefined);
    setError(null);
  }, [open, initialQuery]);

  // Close on Escape for a polished, keyboard-friendly experience.
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const handleSave = async () => {
    setError(null);
    if (type === 'DOCTOR' || type === 'EMPLOYEE') {
      if (!employeeId) {
        setError(type === 'DOCTOR' ? 'Please select a doctor.' : 'Please select an employee.');
        return;
      }
    } else {
      if (!name.trim()) {
        setError('Referral name is required.');
        return;
      }
      if (!mobile.trim()) {
        setError('Mobile number is required.');
        return;
      }
      if (!/^\+?[\d\s()-]{6,20}$/.test(mobile.trim())) {
        setError('Enter a valid mobile number (digits, spaces, dashes or parentheses).');
        return;
      }
    }
    if (!(Number(defaultSharePercent) > 0)) {
      setError('Default referral % is required and must be greater than 0.');
      return;
    }

    try {
      const payload =
        type === 'DOCTOR' || type === 'EMPLOYEE'
          ? {
              type,
              employeeId,
              name:
                name.trim() ||
                (type === 'DOCTOR' ? doctor?.name : employee?.name) ||
                (type === 'DOCTOR' ? 'Doctor' : 'Employee'),
              defaultSharePercent: Number(defaultSharePercent),
            }
          : {
              type: 'OTHER' as const,
              name: name.trim(),
              mobileNumber: mobile.trim(),
              organization: organization.trim() || undefined,
              designation: designation.trim() || undefined,
              defaultSharePercent: Number(defaultSharePercent),
            };
      const res = await mutation.mutateAsync(payload);
      onCreated(res.data);
    } catch (err: any) {
      setError(err?.message || 'Failed to create referral partner.');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 modal-overlay-enter">
      <div className="bg-surface rounded-2xl border border-border shadow-2xl w-full max-w-lg max-h-[88vh] flex flex-col modal-panel-enter">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border bg-background/50 rounded-t-2xl shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
              <Handshake size={18} className="text-primary" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-text truncate">Add Referral</h2>
              <p className="text-xs text-text-muted truncate">
                Quick-register a referrer without leaving the receipt
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-1.5 hover:bg-background rounded-lg transition-colors shrink-0"
          >
            <X size={18} className="text-text-muted" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {error && (
            <div className="flex items-start gap-2 p-2.5 bg-danger/10 border border-danger/20 rounded-lg text-sm text-danger">
              <AlertCircle size={15} className="shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {/* Referral type */}
          <div>
            <SectionHeader icon={Users} title="Referral type" />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
              <TypeCard
                active={type === 'DOCTOR'}
                icon={Stethoscope}
                title="Doctor"
                description="A hospital or outside doctor"
                onClick={() => setType('DOCTOR')}
              />
              <TypeCard
                active={type === 'EMPLOYEE'}
                icon={UserRound}
                title="Employee"
                description="In-house staff member"
                onClick={() => setType('EMPLOYEE')}
              />
              <TypeCard
                active={type === 'OTHER'}
                icon={Building2}
                title="Other"
                description="Clinic, agent or any other referrer"
                onClick={() => setType('OTHER')}
              />
            </div>
          </div>

          {/* Referrer details */}
          <div>
            <SectionHeader
              icon={type === 'DOCTOR' ? Stethoscope : type === 'EMPLOYEE' ? UserRound : Building2}
              title="Referrer details"
            />
            {type === 'DOCTOR' ? (
              <div>
                <label className={labelClass}>Doctor *</label>
                <DoctorSearchSelect
                  value={employeeId}
                  onChange={(id, doc) => {
                    setEmployeeId(id);
                    setDoctor(doc);
                  }}
                  showPreview={false}
                />
                {doctor && (
                  <p className="mt-1 text-xs text-text-muted">
                    Will register <span className="font-medium text-text">{doctor.name}</span> as a doctor referrer.
                  </p>
                )}
              </div>
            ) : type === 'EMPLOYEE' ? (
              <div>
                <label className={labelClass}>Employee *</label>
                <EmployeeSearchSelect
                  value={employeeId}
                  onChange={(id, emp) => {
                    setEmployeeId(id);
                    setEmployee(emp);
                  }}
                  showPreview={false}
                />
                {employee && (
                  <p className="mt-1 text-xs text-text-muted">
                    Will register <span className="font-medium text-text">{employee.name}</span> as an employee referrer.
                  </p>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <label className={labelClass} htmlFor="quick-referral-name">
                    Name *
                  </label>
                  <input
                    id="quick-referral-name"
                    type="text"
                    autoFocus
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Dr. Md. Rafiqul Islam"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass} htmlFor="quick-referral-mobile">
                    Mobile *
                  </label>
                  <input
                    id="quick-referral-mobile"
                    type="tel"
                    inputMode="tel"
                    value={mobile}
                    onChange={(e) => setMobile(e.target.value)}
                    placeholder="+8801…"
                    className={inputClass}
                  />
                </div>
                <div>
                  <DesignationSearchSelect
                    id="quick-referral-designation"
                    label="Designation"
                    value={designation}
                    onChange={setDesignation}
                    placeholder="Search designation…"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelClass} htmlFor="quick-referral-organization">
                    Organization
                  </label>
                  <input
                    id="quick-referral-organization"
                    type="text"
                    value={organization}
                    onChange={(e) => setOrganization(e.target.value)}
                    placeholder="Clinic / hospital / company"
                    className={inputClass}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Compensation */}
          <div>
            <SectionHeader icon={Percent} title="Referral compensation" />
            <label className={labelClass} htmlFor="quick-referral-share">
              Default referral % (legacy)
            </label>
            <div className="relative sm:max-w-[180px]">
              <ValidatedNumberInput
                id="quick-referral-share"
                value={defaultSharePercent}
                onChange={setDefaultSharePercent}
                max={100}
                placeholder="0.00"
                className={`${inputClass} pr-8`}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-text-muted pointer-events-none">
                %
              </span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t border-border px-4 py-3 shrink-0 bg-background/50 rounded-b-2xl">
          <button
            type="button"
            onClick={onClose}
            className="px-3.5 py-2 text-text-muted hover:text-text transition-colors text-sm flex items-center gap-2"
          >
            <X size={16} />
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={mutation.isPending}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-2"
          >
            {mutation.isPending ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            Save Referral
          </button>
        </div>
      </div>
    </div>
  );
}
